import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quotesService } from '../../services/quotesService';
import { workflowStagesService } from '../../services';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';
import QuoteMessagesModal from './QuoteMessagesModal';
import { formatDateRange } from '../../utils/dateUtils';

// Voyager Pro reskin (Figma QuotesManagementPage) - Kanban-style pipeline
// board grouped by quote `status` (draft/sent/accepted/rejected/converted),
// replacing the old flat table. See memory entity "TPCRM Voyager Pro CRM
// Reskin Project" for full rationale. This component is shared by BOTH
// agency (/agent/quotes) and independent (/independent/quotes) agents -
// quotesService.js is already fully unified (no independent branching
// needed there), so only navigation targets and the agency-only
// workflow-stage feature need to branch on user type here.
//
// Design note: columns group by `status`, not by the agency's dynamic
// `workflow_stage` (unlike Figma's mock, which hardcodes a 6-stage
// pipeline) - status is a universal enum that works identically for both
// agent types today, whereas workflow stages are an agency-only feature
// independent agents don't have yet. Workflow stage is still shown as a
// secondary badge + the existing "Move Stage" action, for agency only.
// Drag-and-drop between columns was deliberately NOT implemented - several
// status transitions have real side effects (Send emails the client,
// Convert to Itinerary creates a new record) that shouldn't happen from an
// accidental drag; those stay as explicit confirmed actions.

const STATUS_CFG = {
  draft: { label: 'Draft', color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
  sent: { label: 'Sent', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  accepted: { label: 'Accepted', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  rejected: { label: 'Rejected', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-400' },
  converted: { label: 'Converted', color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-500' },
};
const COLUMN_ORDER = ['draft', 'sent', 'accepted', 'rejected', 'converted'];
// Older/legacy status values that still occasionally appear on existing
// rows but aren't part of the core 5-stage pipeline - grouped into an
// overflow column so nothing is silently hidden.
const OTHER_STATUSES = ['expired', 'deposit_paid', 'paid'];

const fmtMoney = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const initials = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

// ─── Quote card ────────────────────────────────────────────────────────────

function QuoteCard({ quote, selected, onToggleSelect, onOpen, bulkMode }) {
  const notYetOpened = quote.status === 'sent' && !quote.view_count;
  return (
    <div
      onClick={() => bulkMode ? onToggleSelect(quote.id) : onOpen(quote)}
      className={`bg-white border rounded-xl p-3.5 cursor-pointer transition-colors hover:border-[var(--accent)] ${selected ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]' : 'border-[var(--border)]'}`}
    >
      <div className="flex items-start gap-2.5">
        {bulkMode && (
          <input type="checkbox" checked={selected} onChange={() => onToggleSelect(quote.id)} onClick={e => e.stopPropagation()} className="mt-1 flex-shrink-0" />
        )}
        <div className="w-8 h-8 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{initials(quote.client_name)}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[var(--foreground)] truncate">{quote.client_name || 'Unnamed Client'}</div>
          <div className="text-xs text-[var(--muted-foreground)] truncate">{quote.title}</div>
        </div>
        {notYetOpened && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-1.5" title="Not yet opened by client" />}
      </div>
      <div className="text-xs text-[var(--muted-foreground)] mt-2">
        {quote.travel_start_date && quote.travel_end_date ? formatDateRange(quote.travel_start_date, quote.travel_end_date) : 'Dates not set'}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-bold text-[var(--foreground)]">{fmtMoney(quote.total_amount)}</span>
        {quote.workflow_stage_name && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{ color: quote.workflow_stage_color, borderColor: quote.workflow_stage_color }}>
            {quote.workflow_stage_name}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Quote detail drawer ───────────────────────────────────────────────────

function QuoteDetailDrawer({
  quote, onClose, isIndependent, canConvertToItinerary,
  onPreview, onMessages, onMoveStage, onEdit, onSend, onMarkAsSent,
  onConvert, onViewPayments, onDelete, onDuplicate, convertingQuoteId, duplicatingQuoteId,
}) {
  const statusCfg = STATUS_CFG[quote.status] || { label: quote.status, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' };
  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[420px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{quote.title}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{quote.quote_number}</div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
          {quote.workflow_stage_name && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border" style={{ color: quote.workflow_stage_color, borderColor: quote.workflow_stage_color }}>
              {quote.workflow_stage_name}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Client</h3>
          <div className="space-y-1 text-sm">
            <div className="font-medium text-[var(--foreground)]">{quote.client_name}</div>
            <div className="text-[var(--muted-foreground)]">{quote.client_email}</div>
          </div>
        </div>
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Trip Details</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travel Dates</span><span className="font-medium text-[var(--foreground)]">{quote.travel_start_date && quote.travel_end_date ? formatDateRange(quote.travel_start_date, quote.travel_end_date) : 'Not set'}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total</span><span className="font-semibold text-[var(--foreground)]">{fmtMoney(quote.total_amount)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Created</span><span className="font-medium text-[var(--foreground)]">{new Date(quote.created_at).toLocaleDateString()}</span></div>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 p-4 border-t border-[var(--border)] space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onPreview(quote)} className="px-3 py-2 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">👁️ View Quote</button>
          <button onClick={() => onMessages(quote)} className="px-3 py-2 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">💬 Messages</button>
          {!isIndependent && <button onClick={() => onMoveStage(quote)} className="px-3 py-2 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">🔀 Move Stage</button>}
          <button onClick={() => onEdit(quote)} className="px-3 py-2 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">✏️ Edit</button>
        </div>
        {quote.status === 'draft' && (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => onSend(quote.id)} className="px-3 py-2.5 rounded-xl text-xs font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--primary)' }}>Send Quote</button>
            <button onClick={() => onMarkAsSent(quote.id)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors" title="Mark as sent without sending email">Mark as Sent</button>
          </div>
        )}
        {canConvertToItinerary(quote) && (
          <button
            onClick={() => onConvert(quote)}
            disabled={convertingQuoteId === quote.id}
            className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            {convertingQuoteId === quote.id ? 'Converting…' : 'Convert to Itinerary'}
          </button>
        )}
        {quote.hasPayments && (
          <button onClick={() => onViewPayments(quote)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">View Payments ({quote.payment_count})</button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onDuplicate(quote)} disabled={duplicatingQuoteId === quote.id} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors disabled:opacity-50">{duplicatingQuoteId === quote.id ? 'Duplicating…' : '📄 Duplicate Quote'}</button>
          <button onClick={() => onDelete(quote.id)} className="px-3 py-2.5 rounded-xl text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors">Delete Quote</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const QuotesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isIndependent = user?.role === 'independent_agent';
  const basePath = isIndependent ? '/independent' : '/agent';

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [workflowStages, setWorkflowStages] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [duplicatingQuoteId, setDuplicatingQuoteId] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [quoteForConversion, setQuoteForConversion] = useState(null);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [quoteForPaymentDetails, setQuoteForPaymentDetails] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [quoteForStage, setQuoteForStage] = useState(null);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [quoteForMessages, setQuoteForMessages] = useState(null);

  useEffect(() => {
    fetchQuotes();
    if (!isIndependent) fetchWorkflowStages();
    // eslint-disable-next-line
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await quotesService.getAll();
      setQuotes(data);
      setError('');
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowStages = async () => {
    try {
      const stages = await workflowStagesService.getStages();
      setWorkflowStages(stages);
    } catch (err) {
      console.error('Failed to fetch workflow stages:', err);
    }
  };

  const canConvertToItinerary = (quote) => quote.status && !['draft', 'converted', 'expired'].includes(quote.status);

  const filteredQuotes = quotes.filter(quote => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      quote.title?.toLowerCase().includes(term) ||
      quote.client_name?.toLowerCase().includes(term) ||
      quote.quote_number?.toLowerCase().includes(term);
    const matchesSource = sourceFilter === 'all' || quote.source === sourceFilter;
    return matchesSearch && matchesSource;
  });

  const columns = COLUMN_ORDER.map(status => ({
    status,
    cfg: STATUS_CFG[status],
    quotes: filteredQuotes.filter(q => q.status === status),
  }));
  const otherQuotes = filteredQuotes.filter(q => OTHER_STATUSES.includes(q.status));

  // ── Actions ────────────────────────────────────────────────────────────
  const openQuote = (quote) => setSelectedQuote(quote);
  const closeDrawer = () => setSelectedQuote(null);

  const handlePreview = (quote) => navigate(`${basePath}/quotes/${quote.id}/preview`);
  const handleEdit = (quote) => navigate(isIndependent ? `${basePath}/quotes/${quote.id}/edit` : `${basePath}/quotes/${quote.id}`);

  const handleViewMessages = (quote) => { setQuoteForMessages(quote); setShowMessagesModal(true); };
  const closeMessagesModal = () => { setShowMessagesModal(false); setQuoteForMessages(null); };

  const handleMoveStage = (quote) => { setQuoteForStage(quote); setShowStageModal(true); };

  const handleMoveQuoteStage = async (stageId) => {
    if (!quoteForStage) return;
    try {
      await quotesService.moveStage(quoteForStage.id, stageId);
      const stage = workflowStages.find(s => s.id === stageId);
      setQuotes(prev => prev.map(q => q.id === quoteForStage.id ? { ...q, workflow_stage_id: stageId, workflow_stage_name: stage?.name, workflow_stage_color: stage?.color } : q));
      setShowStageModal(false);
      setQuoteForStage(null);
    } catch (err) {
      console.error('Failed to move quote stage:', err);
      alert('Failed to move quote to new stage. Please try again.');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await quotesService.send(quoteId, {});
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: 'sent' } : q));
      setSelectedQuote(null);
      alert('Quote sent successfully!');
    } catch (err) {
      console.error('Error sending quote:', err);
      alert('Failed to send quote');
    }
  };

  const handleMarkAsSent = async (quoteId) => {
    try {
      await quotesService.markAsSent(quoteId);
      setSelectedQuote(null);
      await fetchQuotes();
    } catch (err) {
      console.error('Error marking quote as sent:', err);
      setError('Failed to mark quote as sent');
    }
  };

  const openConvertDialog = (quote) => { setQuoteForConversion(quote); setShowConvertDialog(true); };
  const closeConvertDialog = () => { setShowConvertDialog(false); setQuoteForConversion(null); };

  const handleConvertToItinerary = async () => {
    if (!quoteForConversion) return;
    try {
      setConvertingQuoteId(quoteForConversion.id);
      await quotesService.convertToItinerary(quoteForConversion.id);
      setQuotes(prev => prev.map(q => q.id === quoteForConversion.id ? { ...q, status: 'converted' } : q));
      setShowConvertDialog(false);
      setQuoteForConversion(null);
      setSelectedQuote(null);
      navigate(`${basePath}/itineraries`);
    } catch (err) {
      console.error('Error converting quote:', err);
      alert(err.response?.data?.error || 'Failed to convert quote to itinerary');
    } finally {
      setConvertingQuoteId(null);
    }
  };

  const openPaymentDetailsModal = (quote) => { setQuoteForPaymentDetails(quote); setShowPaymentDetailsModal(true); };
  const closePaymentDetailsModal = () => { setShowPaymentDetailsModal(false); setQuoteForPaymentDetails(null); };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await quotesService.delete(quoteId);
      setQuotes(prev => prev.filter(q => q.id !== quoteId));
      setSelectedQuote(null);
    } catch (err) {
      console.error('Error deleting quote:', err);
      alert('Failed to delete quote');
    }
  };

  const handleDuplicate = async (quote) => {
    if (duplicatingQuoteId) return;
    setDuplicatingQuoteId(quote.id);
    try {
      await quotesService.duplicate(quote.id);
      setSearchTerm('');
      setSourceFilter('all');
      setSelectedQuote(null);
      await fetchQuotes();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error duplicating quote:', err);
      alert('Failed to duplicate quote');
    } finally {
      setDuplicatingQuoteId(null);
    }
  };

  const toggleSelectId = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} selected quote(s)?`)) return;
    try {
      await Promise.all(selectedIds.map(id => quotesService.delete(id)));
      setQuotes(prev => prev.filter(q => !selectedIds.includes(q.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error('Error bulk deleting quotes:', err);
      alert('Failed to delete some quotes');
    }
  };

  const handleBulkMarkSent = async () => {
    try {
      await Promise.all(selectedIds.map(id => quotesService.markAsSent(id)));
      setSelectedIds([]);
      await fetchQuotes();
    } catch (err) {
      console.error('Error bulk marking quotes as sent:', err);
      alert('Failed to mark some quotes as sent');
    }
  };

  const handleExportCsv = () => {
    const rows = [
      ['Quote Number', 'Title', 'Client', 'Client Email', 'Travel Start', 'Travel End', 'Total', 'Status'],
      ...filteredQuotes.map(q => [q.quote_number, q.title, q.client_name, q.client_email, q.travel_start_date, q.travel_end_date, q.total_amount, q.status]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading quotes…</div>;
  }

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedQuote ? 'hidden lg:block' : ''}`}>
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Quotes</h1>
              <p className="text-[var(--muted-foreground)] text-sm">Track quotes through your pipeline, from draft to converted.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={handleExportCsv} className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors">
                Export CSV
              </button>
              <button
                onClick={() => { setBulkMode(!bulkMode); setSelectedIds([]); }}
                className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${bulkMode ? 'bg-[var(--muted)] border-[var(--accent)] text-[var(--foreground)]' : 'bg-white border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/50'}`}
              >
                {bulkMode ? 'Cancel Select' : 'Select'}
              </button>
              <button
                onClick={() => navigate(`${basePath}/quotes/new`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Create New Quote
              </button>
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          {bulkMode && selectedIds.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-[var(--muted)]/60 rounded-xl">
              <span className="text-sm font-semibold text-[var(--foreground)]">{selectedIds.length} selected</span>
              <button onClick={handleBulkMarkSent} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] hover:bg-[var(--muted)] transition-colors">Mark as Sent</button>
              <button onClick={handleBulkDelete} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Delete Selected</button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search by title, client, or quote #…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
              />
            </div>
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Sources</option>
              <option value="manual">Manual</option>
              <option value="website_contact">Website Contact</option>
              <option value="lead_conversion">Lead Conversion</option>
              <option value="api">API</option>
              <option value="import">Import</option>
            </select>
          </div>

          {filteredQuotes.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">📋</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No quotes found</div>
              <div className="text-xs text-[var(--muted-foreground)] mb-4">Create your first quote to get started.</div>
              <button onClick={() => navigate(`${basePath}/quotes/new`)} className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all">Create Your First Quote</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 items-start">
              {columns.map(col => (
                <div key={col.status} className="space-y-2">
                  <div className={`flex items-center justify-between px-3 py-2 rounded-xl border ${col.cfg.bg} ${col.cfg.border}`}>
                    <span className={`text-xs font-bold uppercase tracking-wide ${col.cfg.color}`}>{col.cfg.label}</span>
                    <span className={`text-xs font-bold ${col.cfg.color}`}>{col.quotes.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.quotes.map(quote => (
                      <QuoteCard key={quote.id} quote={quote} selected={selectedIds.includes(quote.id)} onToggleSelect={toggleSelectId} onOpen={openQuote} bulkMode={bulkMode} />
                    ))}
                  </div>
                </div>
              ))}
              {otherQuotes.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-3 py-2 rounded-xl border bg-slate-100 border-slate-200">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Other</span>
                    <span className="text-xs font-bold text-slate-600">{otherQuotes.length}</span>
                  </div>
                  <div className="space-y-2">
                    {otherQuotes.map(quote => (
                      <QuoteCard key={quote.id} quote={quote} selected={selectedIds.includes(quote.id)} onToggleSelect={toggleSelectId} onOpen={openQuote} bulkMode={bulkMode} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedQuote && !bulkMode && (
        <QuoteDetailDrawer
          quote={selectedQuote}
          onClose={closeDrawer}
          isIndependent={isIndependent}
          canConvertToItinerary={canConvertToItinerary}
          onPreview={handlePreview}
          onMessages={handleViewMessages}
          onMoveStage={handleMoveStage}
          onEdit={handleEdit}
          onSend={handleSendQuote}
          onMarkAsSent={handleMarkAsSent}
          onConvert={openConvertDialog}
          onViewPayments={openPaymentDetailsModal}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          convertingQuoteId={convertingQuoteId}
          duplicatingQuoteId={duplicatingQuoteId}
        />
      )}

      {/* Convert to Itinerary Confirmation Dialog */}
      {showConvertDialog && quoteForConversion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeConvertDialog}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Convert Quote to Itinerary</h2>
              <button onClick={closeConvertDialog} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="px-6 py-5">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4 text-sm text-amber-900">
                <p className="font-bold mb-1.5">Important:</p>
                <p className="mb-1.5">This will convert your quote to an Itinerary. We recommend doing this only after you've received payment from the client.</p>
                <p>If you don't need to receive payment for this quote, or already have, please proceed.</p>
              </div>
              <div className="p-4 bg-[var(--muted)]/40 rounded-xl text-sm">
                <div className="font-semibold text-[var(--foreground)]">{quoteForConversion.title}</div>
                <div className="text-[var(--muted-foreground)]">{quoteForConversion.client_name} · {fmtMoney(quoteForConversion.total_amount)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--border)]">
              <button onClick={closeConvertDialog} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
              <button
                onClick={handleConvertToItinerary}
                disabled={convertingQuoteId === quoteForConversion.id}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                {convertingQuoteId === quoteForConversion.id ? 'Converting…' : 'Confirm Conversion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Move Modal (agency only) */}
      {showStageModal && quoteForStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowStageModal(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Move Quote to Different Stage</h2>
              <button onClick={() => setShowStageModal(false)} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="px-6 py-5">
              <div className="p-4 bg-[var(--muted)]/40 rounded-xl mb-4 text-sm">
                <div className="font-semibold text-[var(--foreground)]">{quoteForStage.title}</div>
                <div className="text-[var(--muted-foreground)]">Client: {quoteForStage.client_name}</div>
                <div className="text-[var(--muted-foreground)]">Current Stage: {quoteForStage.workflow_stage_name || 'None'}</div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {workflowStages.map(stage => {
                  const isCurrent = quoteForStage.workflow_stage_id === stage.id;
                  return (
                    <button
                      key={stage.id}
                      onClick={() => handleMoveQuoteStage(stage.id)}
                      disabled={isCurrent}
                      className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${isCurrent ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[var(--muted)]/40'}`}
                      style={{ borderColor: stage.color, backgroundColor: isCurrent ? `${stage.color}20` : 'transparent' }}
                    >
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                      <span className="flex-1 text-left truncate">{stage.name}</span>
                      {isCurrent && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: stage.color }}>Current</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-[var(--border)]">
              <button onClick={() => setShowStageModal(false)} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPaymentDetailsModal && quoteForPaymentDetails && (
        <SecurePaymentDetailsModal isOpen={showPaymentDetailsModal} quote={quoteForPaymentDetails} onClose={closePaymentDetailsModal} />
      )}

      {showMessagesModal && quoteForMessages && (
        <QuoteMessagesModal isOpen={showMessagesModal} quote={quoteForMessages} onClose={closeMessagesModal} />
      )}
    </div>
  );
};

export default QuotesList;
